import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Trash2, ArrowDown, ArrowUp } from 'lucide-react';
import { DailyExpense } from '../../types/cash-register.types';
import { toast } from 'sonner';
import { ConfirmModal } from '@/components/ui/confirm-modal';

interface DailyExpensesProps {
  expenses: DailyExpense[];
  onAddExpense: () => void;
  onAddIncome: () => void;
  onDeleteExpense: (expenseId: string) => Promise<void>;
}

export const DailyExpenses: React.FC<DailyExpensesProps> = ({ expenses, onAddExpense, onAddIncome, onDeleteExpense }) => {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ isOpen: boolean; expenseId: string | null; isIncome: boolean }>({
    isOpen: false,
    expenseId: null,
    isIncome: false
  });

  const formatCurrency = (amount: number): string => {
    return `S/ ${amount.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const expenseItems = expenses.filter(expense => expense.type === 'expense');
  const incomeItems = expenses.filter(expense => expense.type === 'income');

  const totalExpenses = expenseItems.reduce((sum, expense) => sum + expense.amount, 0);
  const totalIncome = incomeItems.reduce((sum, income) => sum + income.amount, 0);
  const netAmount = totalIncome - totalExpenses;

  const getCategoryLabel = (category: string): string => {
    const labels = {
      ingredients: 'Ingredientes',
      services: 'Servicios',
      others: 'Otros',
      income: 'Ingreso adicional',
    };
    return labels[category as keyof typeof labels] || category;
  };

  const handleDeleteClick = (expense: DailyExpense) => {
    setConfirmDelete({
      isOpen: true,
      expenseId: expense.id,
      isIncome: expense.type === 'income'
    });
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete.expenseId) return;

    try {
      setDeletingId(confirmDelete.expenseId);
      await onDeleteExpense(confirmDelete.expenseId);
      setConfirmDelete({ isOpen: false, expenseId: null, isIncome: false });
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Error al eliminar el movimiento. Inténtalo de nuevo.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDelete({ isOpen: false, expenseId: null, isIncome: false });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Movimientos del Día</CardTitle>
          <div className="flex gap-3">
            <Button
              onClick={onAddIncome}
              className="bg-green-600 hover:bg-green-700 text-white h-12 px-6 text-base font-semibold shadow-md transition-all duration-200 active:scale-95 touch-manipulation"
            >
              <ArrowUp className="w-5 h-5 mr-2" />
              Ingreso
            </Button>
            <Button
              onClick={onAddExpense}
              className="bg-red-600 hover:bg-red-700 text-white h-12 px-6 text-base font-semibold shadow-md transition-all duration-200 active:scale-95 touch-manipulation"
            >
              <ArrowDown className="w-5 h-5 mr-2" />
              Gasto
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {expenses.length > 0 ? (
          <div className="space-y-3">
            {expenses.map((expense) => (
              <div key={expense.id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0 group">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    {expense.type === 'income' ? (
                      <ArrowUp className="w-4 h-4 text-green-600" />
                    ) : (
                      <ArrowDown className="w-4 h-4 text-red-600" />
                    )}
                    <div>
                      <p className="font-medium text-sm">{expense.description}</p>
                      <p className="text-xs text-gray-500">{getCategoryLabel(expense.category)}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 min-w-[120px] justify-end">
                  <span className={`font-semibold text-right ${expense.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                    {expense.type === 'income' ? '+' : '-'}{formatCurrency(expense.amount)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteClick(expense)}
                    disabled={deletingId === expense.id}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                  >
                    {deletingId === expense.id ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
            
            <div className="mt-4 pt-3 border-t border-gray-200 space-y-2">
              {totalIncome > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-green-600">Total Ingresos:</span>
                  <div className="min-w-[120px] flex justify-end">
                    <span className="text-green-600 text-right">+{formatCurrency(totalIncome)}</span>
                  </div>
                </div>
              )}
              {totalExpenses > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-red-600">Total Gastos:</span>
                  <div className="min-w-[120px] flex justify-end">
                    <span className="text-red-600 text-right">-{formatCurrency(totalExpenses)}</span>
                  </div>
                </div>
              )}
              <div className="flex justify-between items-center font-bold border-t pt-2">
                <span>Movimiento Neto:</span>
                <div className="min-w-[120px] flex justify-end">
                  <span className={`text-right ${netAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {netAmount >= 0 ? '+' : ''}{formatCurrency(netAmount)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500">
            <p className="mb-2">No hay movimientos registrados</p>
            <p className="text-sm">Presiona "Ingreso" o "Gasto" para registrar movimientos</p>
          </div>
        )}
      </CardContent>

      <ConfirmModal
        isOpen={confirmDelete.isOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={confirmDelete.isIncome ? 'Eliminar ingreso' : 'Eliminar gasto'}
        message={confirmDelete.isIncome
          ? '¿Estás seguro de que quieres eliminar este ingreso? Esta acción no se puede deshacer.'
          : '¿Estás seguro de que quieres eliminar este gasto? Esta acción no se puede deshacer.'
        }
        confirmText="Eliminar"
        variant="danger"
        loading={deletingId !== null}
      />
    </Card>
  );
};